import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useAxios } from '../context/AxiosContext';
import { MarketingSearchType } from '../components/Marketing/MarketingSearchNavigation/MarketingSearch';
import { uuid } from 'uuidv4';
import { MarketingProfileAction, MarketingSearchResponce } from '../@types/Marketing';
import { PROFILE_CACHE_KEY } from './useMarketingProfile';

export const TO_MANY_DESCENDANTS = 'toManyDescendants';

const TO_MANY_DESCENDANTS_ITEM = {
  name: '',
  subRows: [],
  descendants: []
};

const processIncomingData = (data: any[]) => {
  data.forEach((el) => {
    if (el?.toManyDescendants) el.subRows.push({ id: `${TO_MANY_DESCENDANTS}_${uuid()}`, ...TO_MANY_DESCENDANTS_ITEM });
    if (el?.subRows?.length) processIncomingData(el?.subRows);
  });
};

export function useMarketingSearch(searchBy: MarketingSearchType, term: string) {
  const { authAxios } = useAxios();
  const queryClient = useQueryClient();

  const fetchMarketingHierarchy = useCallback(
    async ({ searchBy, term }) => {
      const { data } = await authAxios.get<MarketingSearchResponce>('/marketing/search', {
        params: {
          term,
          groupsOnly: searchBy === MarketingSearchType.GROUP
        }
      });
      processIncomingData(data.tableData);
      return data;
    },
    [authAxios]
  );

  return useQuery<MarketingSearchResponce>(
    ['marketingSearch', searchBy, term],
    () => fetchMarketingHierarchy({ searchBy, term }),
    {
      refetchOnWindowFocus: false,
      enabled: !!term,
      initialData: { isOverflowed: false, tableData: [] },
      onSuccess: async () => {
        await queryClient.invalidateQueries(PROFILE_CACHE_KEY);
      }
    }
  );
}

export function useExсludeFromMarketing() {
  const { authAxios } = useAxios();

  const includeOrExcludeFromMarketing = useCallback(
    async ({ id, exclude }: { id: string; exclude: boolean }) => {
      return authAxios.post('/marketing/toggle-marketing-exclude', {
        id,
        action: exclude ? MarketingProfileAction.EXCLUDE : MarketingProfileAction.INCLUDE
      });
    },
    [authAxios]
  );
  const includeOrExcludeFromMarketingMutation = useMutation(includeOrExcludeFromMarketing);

  return {
    includeOrExcludeFromMarketing: includeOrExcludeFromMarketingMutation.mutateAsync,
    isLoading: includeOrExcludeFromMarketingMutation.isLoading
  };
}

export function useMarketingSearchCache() {
  const queryClient = useQueryClient();

  const invalidateMarketingSearchCache = useCallback(
    () => queryClient.invalidateQueries('marketingSearch'),
    [queryClient]
  );

  return {
    invalidateMarketingSearchCache
  };
}
